import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
} from 'react';

import { environment } from '@trello/config';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';
import { RouteId, useIsActiveRoute } from '@trello/router';
import {
  LazyRovoChatInterface,
  ROVO_SITE_ID_STORAGE_KEY,
  SitePickerScreen,
  useIsRovoIslandEnabled,
  usePrefetchRovoChatInterface,
} from '@trello/rovo';
import { TrelloStorage } from '@trello/storage';
import type { WorkspaceNavigationTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { IslandNav } from 'app/src/components/IslandNav';
import { RovoButton } from 'app/src/components/RovoButton';

import styles from './RovoIsland.less';

interface RovoIslandProps {
  workspaceId: string;
}

export const RovoIsland: FunctionComponent<RovoIslandProps> = ({
  workspaceId,
}) => {
  usePrefetchRovoChatInterface();
  const isCardRoute = useIsActiveRoute(RouteId.CARD);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const isRovoIslandEnabled = useIsRovoIslandEnabled();

  const isProd = environment === 'prod';

  const { triggerRef, toggle, popoverProps } = usePopover({
    placement: PopoverPlacement.TOP_END,
  });

  const toggleRovoChat = useCallback(() => {
    // TODO: Add analytics
    toggle();
  }, [toggle]);

  const onConfirmSite = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    TrelloStorage.set(ROVO_SITE_ID_STORAGE_KEY, siteId);
  }, []);

  useEffect(() => {
    const savedSiteId = TrelloStorage.get(ROVO_SITE_ID_STORAGE_KEY);

    if (!savedSiteId && isProd) {
      setSelectedSiteId('a436116f-02ce-4520-8fbb-7301462a1674');
    } else if (savedSiteId) {
      setSelectedSiteId(savedSiteId);
    }
  }, [isProd]);

  if (!isRovoIslandEnabled || !workspaceId) {
    return null;
  }

  return (
    <IslandNav
      buttonGroupClassName={styles.buttonGroup}
      className={cx({
        [styles.container]: true,
        [styles.isCardRoute]: isCardRoute,
      })}
      data-testid={getTestId<WorkspaceNavigationTestIds>('rovo-island')}
    >
      <RovoButton
        isActive={popoverProps.isVisible}
        onClick={toggleRovoChat}
        triggerRef={triggerRef}
      />
      <Popover
        {...popoverProps}
        noHorizontalPadding
        noVerticalPadding
        size={selectedSiteId ? 'xlarge' : 'large'}
      >
        <div
          className={cx({
            [styles.chatInterfaceContainer]: selectedSiteId,
          })}
        >
          {selectedSiteId ? (
            <LazyRovoChatInterface
              closeChat={toggle}
              selectedSiteId={selectedSiteId}
              workspaceId={workspaceId}
              onConfirmSite={onConfirmSite}
            />
          ) : (
            <SitePickerScreen onConfirmSite={onConfirmSite} />
          )}
        </div>
      </Popover>
    </IslandNav>
  );
};
